/* eslint-disable no-console */
import React, { useRef, useState, useCallback, useEffect } from 'react';
import Webcam from 'react-webcam';
import { faVideo } from '@fortawesome/pro-solid-svg-icons';
import {
  WebcamWrapper,
  RecordIconNotes,
  FaceMarker,
  ContentModal,
  StartRecordButton,
  VideoIcon,
  StyledAlertMessage,
  RecordLabelWrapper,
  PlaceholderVideo,
} from './styles';
import RecordButton from '../icons/record-button.svg';
import { alertDialog as alert } from '../../Modals/AlertDialog';

enum RecordingState {
  NOT_RECORDING,
  RECORDING,
  FINISHED_RECORDING,
}

enum CameraPermissionState {
  UNDEFINED,
  GAINED_PERMISSION,
  DENIED_PERMISSION,
}

const CAMERA_WIDTH = 480;
const CAMERA_LENGTH = 480;

const videoConstraints = {
  facingMode: 'user',
  width: CAMERA_WIDTH,
  height: CAMERA_LENGTH,
};

const RECORDING_COUNTDOWN_SECONDS = 5;

const isMediaRecorderSupported = (): boolean => {
  return typeof MediaRecorder !== 'undefined';
};

let mimeType = '';

if (isMediaRecorderSupported()) {
  mimeType = MediaRecorder.isTypeSupported('video/webm')
    ? 'video/webm'
    : MediaRecorder.isTypeSupported('video/mp4')
    ? 'video/mp4'
    : '';
}

interface WebcamRecorderProps {
  uploading: boolean;
  onFinishedRecording: (blob: Blob) => void;
}

export const WebcamRecorder: React.FC<WebcamRecorderProps> = ({
  uploading,
  onFinishedRecording,
}) => {
  const webcamRef = useRef<null | Webcam>(null);
  const mediaRecorderRef = useRef<null | MediaRecorder>(null);
  const [recordedChunks, setRecordedChunks] = useState([]);
  const [
    cameraPermissionState,
    setCameraPermissionState,
  ] = useState<CameraPermissionState>(CameraPermissionState.UNDEFINED);

  const [recordingState, setRecordingState] = useState<RecordingState>(
    RecordingState.NOT_RECORDING
  );

  const handleDataAvailable = useCallback(
    ({ data }) => {
      if (data.size > 0) {
        setRecordedChunks((prev) => prev.concat(data));
      }
    },
    [setRecordedChunks]
  );

  const startRecording = useCallback(() => {
    if (!isMediaRecorderSupported()) {
      alert({
        title: 'error',
        message: 'The browser is not supported. Please use latest version',
      });
      return;
    }
    if (webcamRef.current && webcamRef.current.stream) {
      mediaRecorderRef.current = new MediaRecorder(webcamRef.current.stream, {
        mimeType,
      });
      mediaRecorderRef.current.addEventListener(
        'dataavailable',
        handleDataAvailable
      );
      mediaRecorderRef.current.start();
      setRecordingState(RecordingState.RECORDING);
    }
  }, [webcamRef, mediaRecorderRef, setRecordingState]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
      setRecordingState(RecordingState.FINISHED_RECORDING);
    }
  }, [mediaRecorderRef, webcamRef, setRecordingState]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handlerPermissionError = (err: any): void => {
    if (err.message === 'Permission dismissed') {
      // manually asking the user camera permission again
      navigator.mediaDevices
        .getUserMedia({ video: videoConstraints })
        .then((mediaStream) => {
          if (webcamRef.current?.video) {
            setCameraPermissionState(CameraPermissionState.GAINED_PERMISSION);
            const { video } = webcamRef.current;
            webcamRef.current.stream = mediaStream;
            video.srcObject = mediaStream;
            video.onloadedmetadata = function () {
              video.play();
            };
          }
        })
        .catch(handlerPermissionError);
    } else if (err.message === 'Permission denied') {
      // Case where 'Permission denied'
      setCameraPermissionState(CameraPermissionState.DENIED_PERMISSION);
    }
  };

  useEffect(() => {
    if (recordingState === RecordingState.RECORDING) {
      setTimeout(stopRecording, RECORDING_COUNTDOWN_SECONDS * 1000);
    }
  }, [recordingState]);

  useEffect(() => {
    if (recordedChunks.length) {
      const blob = new Blob(recordedChunks, {
        type: mimeType,
      });
      onFinishedRecording(blob);
      setRecordedChunks([]);
    }
  }, [recordedChunks]);

  if (cameraPermissionState === CameraPermissionState.DENIED_PERMISSION) {
    return (
      <>
        <StyledAlertMessage
          kind="error"
          title="Camera access permission is blocked"
          message="Please allow the camera permission for this site from your browser settings!"
        />
      </>
    );
  }

  return (
    <div>
      <StyledAlertMessage
        kind="info"
        message={
          <>
            Face forward at your camera and look directly into it. When you are
            ready, press the red button to start recording. <br /> Video face
            capture will take approximately 5 seconds.
          </>
        }
      />
      <ContentModal>
        <WebcamWrapper>
          <Webcam
            ref={webcamRef}
            audio={false}
            width={CAMERA_WIDTH}
            height={CAMERA_LENGTH}
            videoConstraints={videoConstraints}
            mirrored
            onUserMedia={() =>
              setCameraPermissionState(CameraPermissionState.GAINED_PERMISSION)
            }
            onUserMediaError={handlerPermissionError}
          />
          <PlaceholderVideo />
          <FaceMarker />
        </WebcamWrapper>
        <RecordLabelWrapper>
          {recordingState === RecordingState.NOT_RECORDING && (
            <StartRecordButton onClick={startRecording}>
              <img src={RecordButton} alt="start-record" />
            </StartRecordButton>
          )}
          {recordingState === RecordingState.RECORDING && (
            <RecordIconNotes>
              <VideoIcon icon={faVideo} />
              <span>{'Recording...'} </span>
            </RecordIconNotes>
          )}
          {uploading && (
            <RecordIconNotes>
              <span>{'Uploading...'} </span>
            </RecordIconNotes>
          )}
        </RecordLabelWrapper>
      </ContentModal>
    </div>
  );
};
